import React, { useMemo } from 'react';
import { CategoryImage } from '@/components/category-image';
import { createStyles } from '@/contexts/theme-context';
import { getCategoryImageURL } from '@/utils/category';
import { ListOption } from '@/components/list-item';
import { partnerSupplier } from '@/utils/environment';
import { PixelRatio, Text, TouchableOpacity, View } from 'react-native';
import { shadow, TYPOGRAPHY } from '@/styles';
import { SupplierCatalogCategoryType } from '@/contexts/item-context-types';
import { useLayoutContext } from '@/contexts/layout-context';
import { useUserContext } from '@/contexts/user-context';

const LIST_ITEM_PADDING_VERTICAL = 8;

const BASE_NUMBER_OF_LINES = 3;
const IMAGE_NUMBER_OF_LINES = 2;

const BORDER_BOTTOM_WIDTH = 4;
const IMAGE_HEIGHT = 80;
const LIST_PADDING_HORIZONTAL = 16;
const LIST_PADDING_HORIZONTAL_LARGE = 32;
const TEXT_TOP_PADDING = 8;
// top border is already BORDER_BOTTOM_WIDTH of background color
const TEXT_BOTTOM_PADDING = TEXT_TOP_PADDING - BORDER_BOTTOM_WIDTH;

const BASE_ITEM_HEIGHT = 88;
const IMAGE_ITEM_HEIGHT =
  2 * LIST_ITEM_PADDING_VERTICAL +
  2 * BORDER_BOTTOM_WIDTH +
  +(
    TYPOGRAPHY.pBold.lineHeight *
    PixelRatio.getFontScale() *
    IMAGE_NUMBER_OF_LINES
  ) +
  IMAGE_HEIGHT +
  TEXT_BOTTOM_PADDING +
  TEXT_TOP_PADDING;

export type CategoryListOption = Omit<ListOption, 'value'> & {
  value: SupplierCatalogCategoryType;
};

type Edge = 'left' | 'right' | null;

interface Props {
  edge: Edge;
  onPress: (category: SupplierCatalogCategoryType) => void;
  option: CategoryListOption;
}

export const useCategoryListItemHeight = () => {
  return partnerSupplier.demoEnabled ? IMAGE_ITEM_HEIGHT : BASE_ITEM_HEIGHT;
};

export const SupplierCatalogCategoryListItem: React.FC<Props> = ({
  edge,
  onPress,
  option,
}) => {
  const { isSmallLayout } = useLayoutContext();
  const [styles] = useStyles();
  const itemHeight = useCategoryListItemHeight();
  const { selectedRestaurantRelationship } = useUserContext();

  const catalogImagesEnabled = partnerSupplier.demoEnabled;

  const edgeStyle = useMemo(() => {
    if (!edge) {
      return null;
    }

    if (edge === 'left') {
      return isSmallLayout ? styles.baseLeft : styles.baseLeftLarge;
    }

    return isSmallLayout ? styles.baseRight : styles.baseRightLarge;
  }, [edge, isSmallLayout, styles]);

  return (
    <View
      style={[styles.base, { height: itemHeight }, edgeStyle]}
      testID={`supplier-catalog-category-button-${option.value?.toLowerCase()}`}
    >
      <TouchableOpacity
        onPress={() => onPress(option.value)}
        style={styles.buttonContainer}
      >
        {catalogImagesEnabled ? (
          <View style={styles.categoryImageContainer}>
            <CategoryImage
              category={{
                imageUrl: getCategoryImageURL(
                  selectedRestaurantRelationship.supplierUUID,
                  option.title,
                ),
                name: option.title,
              }}
              imageSize={IMAGE_HEIGHT}
              style={styles.categoryImage}
            />
          </View>
        ) : null}
        <View style={styles.button}>
          <View style={styles.content}>
            <Text
              numberOfLines={
                catalogImagesEnabled
                  ? IMAGE_NUMBER_OF_LINES
                  : BASE_NUMBER_OF_LINES
              }
              style={[TYPOGRAPHY.pBold, styles.text]}
            >
              {option.title}
            </Text>
          </View>
        </View>
      </TouchableOpacity>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    paddingHorizontal: 8,
    paddingVertical: LIST_ITEM_PADDING_VERTICAL,
  },
  baseLeft: {
    paddingLeft: LIST_PADDING_HORIZONTAL,
  },
  baseLeftLarge: {
    paddingLeft: LIST_PADDING_HORIZONTAL_LARGE,
  },
  baseRight: {
    paddingRight: LIST_PADDING_HORIZONTAL,
  },
  baseRightLarge: {
    paddingRight: LIST_PADDING_HORIZONTAL_LARGE,
  },
  button: {
    borderRadius: 4,
    flex: 1,
    overflow: 'hidden',
  },
  buttonContainer: {
    ...shadow({
      color: theme.colors.black,
      elevation: 5,
      opacity: 0.15,
      radius: 4,
      x: 0,
      y: 0,
    }),
    backgroundColor: theme.colors.white,
    borderRadius: 4,
    flex: 1,
  },
  categoryImage: {
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
    height: '100%',
    width: '100%',
  },
  categoryImageContainer: {
    alignItems: 'center',
    height: IMAGE_HEIGHT,
    justifyContent: 'center',
  },
  content: {
    alignItems: 'center',
    borderBottomColor: theme.colors.accent,
    borderBottomWidth: BORDER_BOTTOM_WIDTH,
    borderTopColor: theme.colors.white,
    borderTopWidth: BORDER_BOTTOM_WIDTH,
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
    paddingHorizontal: 12,
  },
  text: {
    textAlign: 'center',
  },
}));
